"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Users, TrendingUp, Handshake, Quote } from "lucide-react";
export default function Benefits() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const supplierSteps = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const steps = [{
    title: "Connect with Buyers",
    description: "Gain immediate visibility to high-volume procurement professionals and global distributors actively seeking verified partners.",
    quote: "KTEX opened doors to markets we couldn't reach on our own.",
    author: "Global Textiles Ltd.",
    icon: Users
  }, {
    title: "Grow Your Business",
    description: "Scale your operations with streamlined quotation workflows and deep market insights that drive higher conversion rates.",
    quote: "Our export volume increased by 40% within the first six months.",
    author: "Elite Manufacturing",
    icon: TrendingUp
  }, {
    title: "Build Long-Term Partnerships",
    description: "Move beyond one-off transactions to establish sustainable, strategic relationships with reliable international buyers.",
    quote: "The platform fosters trust that leads to multi-year contracts.",
    author: "Prime Sourcing Co.",
    icon: Handshake
  }];
  return <section data-section-id="8729" id="supplier-benefits" className="relative overflow-hidden bg-[var(--color-secondary)] py-28 md:py-40">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-40" aria-hidden="true">
        <div className="absolute -top-1/4 -left-1/4 h-[80%] w-[70%] rounded-lg blur-3xl" style={{
        background: "radial-gradient(circle, var(--color-primary) 0%, transparent 60%)",
        animation: "hazeDrift 24s ease-in-out infinite"
      }} />
        <div className="absolute top-1/3 right-0 h-[70%] w-[60%] rounded-lg blur-3xl" style={{
        background: "radial-gradient(circle, var(--color-accent) 0%, transparent 60%)",
        animation: "hazeDrift 24s ease-in-out infinite reverse"
      }} />
      </div>

      <style>{`
        @keyframes hazeDrift {
          0%, 100% {
            transform: scale(1) rotate(0deg);
            filter: hue-rotate(0deg);
          }
          50% {
            transform: scale(1.06) rotate(4deg);
            filter: hue-rotate(8deg);
          }
        }
      `}</style>

      <div className="relative mx-auto max-w-[1240px] px-6 md:px-16">
        <div ref={intro.ref} className="mx-auto max-w-[850px] text-center" style={{
        opacity: intro.isVisible ? 1 : 0,
        transform: intro.isVisible ? "translateY(0)" : "translateY(16px)",
        transition: "opacity 800ms cubic-bezier(0.22, 1, 0.36, 1), transform 800ms cubic-bezier(0.22, 1, 0.36, 1)"
      }}>
          <span className="font-default inline-block text-[0.8125rem] font-semibold uppercase text-[var(--color-accent)]" style={{
          letterSpacing: "0.15em"
        }}>
            Strategic Partnership
          </span>
          <h2 className="font-default mt-6 font-bold text-[var(--color-primary)]" style={{
          fontSize: "clamp(2.25rem, 5vw, 4rem)",
          letterSpacing: "-0.03em",
          lineHeight: 1.1
        }}>
            Elevate Your Global{" "}
            <span className="font-default font-normal italic text-[var(--color-accent)]">
              Market Presence
            </span>
          </h2>
          
          <div className="mt-10 flex flex-col items-center gap-8">
            <p className="font-default max-w-[780px] text-[var(--color-text-dark)]" style={{
            fontSize: "clamp(1.125rem, 1.5vw, 1.35rem)",
            lineHeight: 1.7,
            letterSpacing: "-0.01em"
          }}>
              Join an elite network of verified suppliers gaining direct access to high-volume procurement professionals and global distributors. Our platform facilitates long-term strategic partnerships by streamlining quotation workflows and providing deep market insights that transform sales opportunities into sustainable international growth.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild className="btn-primary min-w-[220px] shadow-lg">
                <Link to="#supplierregistrationform">
                  Apply to Join Network
                </Link>
              </Button>
              <Button asChild className="btn-secondary min-w-[220px] shadow-md">
                <Link to="#supplierbenefitsshowcase">
                  View Partner Benefits
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {}
        <div ref={supplierSteps.ref} className="mt-32" style={{
        opacity: supplierSteps.isVisible ? 1 : 0,
        transform: supplierSteps.isVisible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 1000ms ease-out, transform 1000ms ease-out"
      }}>
          <div className="mb-16 text-center">
            <h3 className="font-default text-3xl font-bold text-[var(--color-primary)] md:text-4xl">
              Why Suppliers Work With KTEX
            </h3>
            <p className="font-default mx-auto mt-4 max-w-2xl text-[var(--color-text-dark)] opacity-80">
              Our ecosystem is designed to support your journey from initial connection to global expansion through a structured, value-driven approach.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            {steps.map((step, i) => <div key={i} data-index={i} className="card flex flex-col h-full">
                <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-lg bg-[var(--color-primary)]/5 text-[var(--color-accent)]">
                  <step.icon className="size-6" />
                </div>
                <h4 className="font-default mb-4 text-xl font-semibold text-[var(--color-primary)]">
                  {step.title}
                </h4>
                <p className="font-default mb-8 flex-grow text-sm leading-relaxed text-[var(--color-text-dark)] opacity-90">
                  {step.description}
                </p>
                
                <div className="mt-auto pt-6 border-t border-[var(--color-border)]">
                  <div className="flex gap-3">
                    <Quote className="size-4 shrink-0 text-[var(--color-accent)] opacity-60" />
                    <div className="space-y-2">
                      <p className="font-default text-xs italic leading-snug text-[var(--color-text-dark)] opacity-70 mb-0">
                        "{step.quote}"
                      </p>
                      <p className="font-default text-[10px] font-bold uppercase tracking-wider text-[var(--color-accent)] mb-0">
                        — {step.author}
                      </p>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}